import React, { useState } from 'react'
import * as _ from 'lodash'
import { useRequirementsManagement } from '../hooks/useRequirementsManagement'
import { QuestionAnsweringForm } from './question-set-answering/QuestionAnsweringForm'
import Loading from '../../core/Loading'
import {
  CredentialType,
  CredentialWithAssociatedEntity,
  QuestionSetPreReq,
  RequirementsProps,
} from '../types'
import WaiversToSign from './WaiversToSign'
import CredentialIds from './CredentialIds'

type OrderedRequirement =
  | { kind: 'identification'; credential: CredentialWithAssociatedEntity; displayOrder: number }
  | { kind: 'waiver'; credential: CredentialWithAssociatedEntity; displayOrder: number }
  | { kind: 'questionnaire'; questionSet: QuestionSetPreReq; displayOrder: number }

const getDisplayOrder = (displayOrder?: number, fallback = Number.MAX_SAFE_INTEGER) =>
  displayOrder != null ? displayOrder : fallback

const buildOrderedRequirements = (
  credentials: CredentialWithAssociatedEntity[],
  questionSets: QuestionSetPreReq[],
): OrderedRequirement[] => {
  const items: OrderedRequirement[] = []

  credentials.forEach(credential => {
    const type = credential.credential.type
    const displayOrder = getDisplayOrder(credential.displayOrder)

    if (type === CredentialType.Identification) {
      items.push({ kind: 'identification', credential, displayOrder })
      return
    }

    if (type === CredentialType.Waiver) {
      items.push({ kind: 'waiver', credential, displayOrder })
    }
  })

  questionSets.forEach(questionSet => {
    items.push({
      kind: 'questionnaire',
      questionSet,
      displayOrder: getDisplayOrder(questionSet.displayOrder),
    })
  })

  return _.sortBy(items, [
    'displayOrder',
    item =>
      item.kind === 'questionnaire' ? item.questionSet.questionSetId : item.credential.credential.id,
  ])
}

export const RequirementsContainer = (props: RequirementsProps) => {
  const { isAdmin, continueEvenIfError, organizationId } = props
  const [excludedQuestionSetIds, setExcludedQuestionSetIds] = useState<Array<number>>([])
  const { loading, requirements, allSet, refresh, answerQuestionSet, person } = useRequirementsManagement({
    excludedQuestionSetIds,
    person: props.person,
    filters: props.filters,
    onCompleted: props.onCompleted,
    continueEvenIfError,
  })
  const { credentials, questionSets } = requirements

  if (loading) {
    return <Loading />
  }

  const onSubmit = (submission: any) => answerQuestionSet(submission.questionSet)

  if (allSet) {
    return <div>All Set!</div>
  }

  const [nextRequirement] = buildOrderedRequirements(credentials, questionSets)

  if (!nextRequirement) {
    return <div>Uh oh! something went wrong</div>
  }

  if (nextRequirement.kind === 'identification') {
    return (
      <CredentialIds
        credentialIds={[nextRequirement.credential]}
        user={person}
        complete={refresh}
        organizationId={organizationId}
      />
    )
  }

  if (nextRequirement.kind === 'waiver') {
    return <WaiversToSign waivers={[nextRequirement.credential]} user={person} complete={refresh} />
  }

  const questionSet = nextRequirement.questionSet
  return (
    <QuestionAnsweringForm
      onSubmit={onSubmit}
      heading={`Please answer the following questions in order to register for '${questionSet.associatedEntity.name}'`}
      questionSet={{ ...questionSet }}
      required={
        isAdmin || questionSet.questions.every(question => !question.isRequired || !!question.disabled)
          ? false
          : questionSet.required
      }
      onSkip={submission =>
        setExcludedQuestionSetIds(_.uniq([...excludedQuestionSetIds, submission.questionSet.questionSetId]))
      }
      organizationId={organizationId as number}
    />
  )
}
